import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { buildPatchPayload, type ParsedApiError } from '@loctopus/shared';
import { ErrorSummary } from '@loctopus/ui';
import { api } from '../../lib/api';
import { toParsedError } from '../../lib/errorDisplay';
import { formatDate } from '../../lib/format';
import type { PromoCode, PromoCodeType } from '../../lib/types';
import {
  Badge,
  Button,
  Card,
  DataTable,
  FormField,
  Input,
  Modal,
  ModuleNotIncluded,
  PageHeader,
  Select,
  Spinner,
} from '../../components/ui';
import { RequirePermission } from '../../lib/auth';
import { useClientPackage } from '../../lib/clientPackage';
import { useEventPicker } from '../shared/useEventPicker';

export function PromoCodesPage() {
  const { eventId, picker } = useEventPicker();
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<PromoCode | null>(null);
  const { hasModule, isLoading: pkgLoading } = useClientPackage();

  const promoQuery = useQuery({
    queryKey: ['promo-codes', eventId],
    queryFn: () => api.promoCodes.list(eventId!),
    enabled: !!eventId,
  });

  // docs/ARCHITECTURE_V2.md §3.4/§12 agent 7: a courtesy-only gate (the server independently
  // enforces its own `promoCodes` module check regardless of what this renders) - shown instead
  // of the whole page rather than hidden, so a client lands on "not included" wording, not a
  // confusing blank/broken screen. Fails open while the package is still loading.
  if (!pkgLoading && !hasModule('promoCodes')) {
    return (
      <div>
        <PageHeader title="Promo codes" />
        <ModuleNotIncluded feature="Promo codes" />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Promo codes"
        description="Percent, fixed-amount and access codes, scoped per event."
        actions={
          <RequirePermission permission="promo_codes.manage">
            <Button
              disabled={!eventId}
              onClick={() => {
                setEditing(null);
                setModalOpen(true);
              }}
            >
              New promo code
            </Button>
          </RequirePermission>
        }
      />
      <Card title="Event" className="mb-6">
        {picker}
      </Card>
      {!eventId ? (
        <p className="text-sm text-slate-500">Choose an event to manage its promo codes.</p>
      ) : promoQuery.isLoading ? (
        <Spinner />
      ) : (
        <DataTable<PromoCode>
          columns={[
            {
              key: 'code',
              header: 'Code',
              render: (p) => <span className="font-mono font-medium">{p.code}</span>,
            },
            { key: 'type', header: 'Type', render: (p) => <Badge>{p.type}</Badge> },
            {
              key: 'value',
              header: 'Value',
              render: (p) =>
                p.type === 'percent'
                  ? `${(p.percentBps ?? 0) / 100}%`
                  : p.type === 'fixed'
                    ? `${((p.fixedAmountMinor ?? 0) / 100).toFixed(2)}`
                    : 'Access only',
            },
            {
              key: 'usage',
              header: 'Usage',
              render: (p) => `${p.usageCount}${p.usageLimit ? ` / ${p.usageLimit}` : ''}`,
            },
            {
              key: 'window',
              header: 'Window',
              render: (p) =>
                p.startsAt || p.endsAt
                  ? `${formatDate(p.startsAt)} - ${formatDate(p.endsAt)}`
                  : 'Always',
            },
          ]}
          rows={promoQuery.data?.data ?? []}
          getRowKey={(p) => p.id}
          onRowClick={(p) => {
            setEditing(p);
            setModalOpen(true);
          }}
          emptyMessage="No promo codes for this event yet."
        />
      )}
      {eventId && (
        // REAL BUG FOUND AND FIXED (same root cause as EventDetailPage.tsx's TicketTypeFormModal,
        // found while live-verifying this round's clear-vs-untouched fix): this modal is always
        // mounted, so its useState initializers only ran once at first mount - "Edit promo code"
        // always opened blank, never the clicked code's real values. `key` forces a real remount
        // per edited record.
        <PromoCodeFormModal
          key={editing?.id ?? 'new'}
          eventId={eventId}
          promoCode={editing}
          open={modalOpen}
          onClose={() => setModalOpen(false)}
        />
      )}
    </div>
  );
}

function PromoCodeFormModal({
  eventId,
  promoCode,
  open,
  onClose,
}: {
  eventId: string;
  promoCode: PromoCode | null;
  open: boolean;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [code, setCode] = useState(promoCode?.code ?? '');
  const [type, setType] = useState<PromoCodeType>(promoCode?.type ?? 'percent');
  const [percent, setPercent] = useState(
    promoCode ? String((promoCode.percentBps ?? 0) / 100) : '10',
  );
  const [fixedAmount, setFixedAmount] = useState(
    promoCode ? String((promoCode.fixedAmountMinor ?? 0) / 100) : '5',
  );
  const [usageLimit, setUsageLimit] = useState(
    promoCode?.usageLimit != null ? String(promoCode.usageLimit) : '',
  );
  const [formError, setFormError] = useState<ParsedApiError | null>(null);

  const mutation = useMutation({
    mutationFn: () => {
      // REAL BUG FOUND AND FIXED (CREATE path): this used to always send percentBps/
      // fixedAmountMinor/usageLimit as `null` for whichever don't apply to the chosen type (or
      // are left blank) - createPromoCodeSchema's `.optional()` fields accept a missing/
      // undefined key, never an explicit null, so saving ANY promo code except one with every
      // field filled in always 400'd. There's no prior value on a CREATE, so each is only
      // included when actually relevant - never sent at all otherwise.
      const current = {
        code: code.toUpperCase(),
        type,
        percentBps: type === 'percent' ? Math.round(Number(percent) * 100) : null,
        fixedAmountMinor: type === 'fixed' ? Math.round(Number(fixedAmount) * 100) : null,
        usageLimit: usageLimit.trim() ? Number(usageLimit) : null,
      };
      if (!promoCode) {
        const body: Partial<PromoCode> = { code: current.code, type: current.type };
        if (current.percentBps !== null) body.percentBps = current.percentBps;
        if (current.fixedAmountMinor !== null) body.fixedAmountMinor = current.fixedAmountMinor;
        if (current.usageLimit !== null) body.usageLimit = current.usageLimit;
        return api.promoCodes.create(eventId, body);
      }
      // FIXED (EDIT path - same documented known limitation as the ticket type/billing plan edit
      // forms, `docs/FORM_SWEEP_RESULTS.md` "Known real gaps" #4): omitting a blank/not-applicable
      // field on edit meant "don't change it" - switching a code's type away from `percent` never
      // cleared its stale `percentBps`, and a usage limit once set could never be lifted back to
      // unlimited. `buildPatchPayload` (`packages/shared/src/buildPatchPayload.ts`) diffs against
      // the promo code as loaded: unchanged fields are omitted, a field cleared to blank is sent
      // as an explicit `null` (now accepted - `updatePromoCodeSchema`'s fields are
      // `.nullable().optional()`), a changed field is sent with its new value.
      const original = {
        code: promoCode.code,
        type: promoCode.type,
        percentBps: promoCode.percentBps ?? null,
        fixedAmountMinor: promoCode.fixedAmountMinor ?? null,
        usageLimit: promoCode.usageLimit ?? null,
      };
      return api.promoCodes.update(promoCode.id, buildPatchPayload(original, current));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['promo-codes', eventId] });
      onClose();
    },
    onError: (err) => setFormError(toParsedError(err)),
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={promoCode ? 'Edit promo code' : 'New promo code'}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => mutation.mutate()} disabled={!code.trim() || mutation.isPending}>
            {mutation.isPending ? 'Saving...' : 'Save'}
          </Button>
        </>
      }
    >
      {formError && <ErrorSummary error={formError} className="mb-3" />}
      <FormField label="Code">
        <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="EARLYBIRD" />
      </FormField>
      <FormField label="Type">
        <Select value={type} onChange={(e) => setType(e.target.value as PromoCodeType)}>
          <option value="percent">Percent off</option>
          <option value="fixed">Fixed amount off</option>
          <option value="access">Access code (no discount)</option>
        </Select>
      </FormField>
      {type === 'percent' && (
        <FormField label="Percent off">
          <Input
            type="number"
            min={0}
            max={100}
            value={percent}
            onChange={(e) => setPercent(e.target.value)}
          />
        </FormField>
      )}
      {type === 'fixed' && (
        <FormField label="Amount off">
          <Input
            type="number"
            min={0}
            step="0.01"
            value={fixedAmount}
            onChange={(e) => setFixedAmount(e.target.value)}
          />
        </FormField>
      )}
      <FormField label="Usage limit" hint="Leave blank for unlimited">
        <Input
          type="number"
          min={1}
          value={usageLimit}
          onChange={(e) => setUsageLimit(e.target.value)}
        />
      </FormField>
    </Modal>
  );
}
